// Ch78 · "see what all Locators are actively working on" — pure model.
//
// Groups the client roster by assigned locator and counts the pipeline
// stages that are WORK (new_client / touring / applied / payment_received).
// out_of_contact and closed are not work and are not counted. A client with
// no locator lands in the `unassigned` bucket, which is the number a manager
// most needs to see.

import type { Agent, Client, ClientStatus } from "../components/clients/types";

export const WORK_STAGES: readonly ClientStatus[] = ["new_client", "touring", "applied", "payment_received"];

export interface LocatorWorkload {
  /** "unassigned" for the no-locator bucket. */
  agentId: string;
  name: string;
  avatarUrl?: string;
  total: number;
  byStage: Record<string, number>;
  /** Clients never contacted at all — the ones a manager nudges about. */
  neverContacted: number;
}

export function buildLocatorWorkload(clients: Client[], agents: Agent[] = []): LocatorWorkload[] {
  const byAgent = new Map<string, LocatorWorkload>();
  const nameOf = new Map(agents.map((a) => [a.id, a]));
  for (const c of clients) {
    if (!WORK_STAGES.includes(c.status)) continue;
    const id = c.assigned_agent_id || "unassigned";
    let row = byAgent.get(id);
    if (!row) {
      const agent = c.assigned_agent ?? nameOf.get(id);
      row = {
        agentId: id,
        name: id === "unassigned" ? "Unassigned" : agent?.full_name || agent?.email || "Locator",
        avatarUrl: agent?.avatar_url,
        total: 0,
        byStage: {},
        neverContacted: 0,
      };
      byAgent.set(id, row);
    }
    row.total += 1;
    row.byStage[c.status] = (row.byStage[c.status] || 0) + 1;
    if (!c.last_contacted_at) row.neverContacted += 1;
  }
  // Busiest first; the unassigned bucket always last so it reads as the gap.
  return Array.from(byAgent.values()).sort((a, b) => {
    if (a.agentId === "unassigned") return 1;
    if (b.agentId === "unassigned") return -1;
    return b.total - a.total || a.name.localeCompare(b.name);
  });
}
